import { useRef, useState } from "react";
import { Send } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { useMentionAutocomplete, getFullName } from "../../utils/mentions";

function initialsFor(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return ((parts[0] || "?")[0] || "?").toUpperCase().padEnd(2, "?");
}

// Props:
//   currentUser
//   onSubmit: (text: string) => Promise<void>
function CommentComposer({ currentUser, onSubmit }) {
  const [{ users }] = useStateValue();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const mention = useMentionAutocomplete({
    text,
    setText,
    users,
    currentUserId: currentUser?.id,
    fieldRef: inputRef,
  });

  const displayName =
    `${currentUser?.firstname || ""} ${currentUser?.lastname || ""}`.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setText("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-start gap-2 pt-2">
      <div className="w-7 h-7 rounded-full bg-slate-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
        {initialsFor(displayName)}
      </div>
      <div className="flex-1 min-w-0 flex gap-2">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={mention.fieldHandlers.onChange}
            placeholder="Write a comment… (type @ to mention)"
            className="w-full border border-gray-200 rounded-full px-3 py-1.5 text-[12.5px] text-slate-800 placeholder-gray-400 focus:outline-none focus:border-slate-400"
          />
          {mention.showMentions && mention.candidates.length > 0 && (
            <div
              className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto z-30"
              onMouseDown={(e) => e.preventDefault()}
            >
              {mention.candidates.map((u) => (
                <button
                  type="button"
                  key={u.id}
                  onClick={() => mention.selectMention(u)}
                  className="w-full text-left p-2 hover:bg-slate-50 flex items-center gap-2"
                >
                  <img
                    src={u.profilepicture || "/assets/user.png"}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span className="text-sm text-slate-800">
                    {getFullName(u)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={!text.trim() || submitting}
          className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-full text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          <Send size={12} />
          {submitting ? "…" : "Post"}
        </button>
      </div>
    </form>
  );
}

export default CommentComposer;
